import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Chip,
  IconButton,
  MenuItem,
  Grid,
  Card,
  CardContent,
  Tooltip,
  Avatar,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Edit as EditIcon,
  PersonAdd as PersonAddIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import {
  getUsers,
  getUser,
  createUser,
  updateUser,
  getUserLevels,
  getSkills,
  assignSkill,
  removeUserSkill,
  updateUserSkill,
  User,
  UserLevel,
  Skill,
  UserSkill,
  CreateUserRequest,
  UpdateUserRequest,
  AssignSkillRequest,
  UpdateUserSkillRequest,
} from '../services/userService';

const PROFICIENCY_LEVELS = ['Beginner', 'Intermediate', 'Advanced', 'Expert'];

export default function UsersPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [userLevels, setUserLevels] = useState<UserLevel[]>([]);
  const [skills, setSkills] = useState<Skill[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [skillsDialogOpen, setSkillsDialogOpen] = useState(false);
  const [addSkillDialogOpen, setAddSkillDialogOpen] = useState(false);
  
  const [filterLevel, setFilterLevel] = useState<string>('');
  const [filterSkill, setFilterSkill] = useState<string>('');
  
  const [createForm, setCreateForm] = useState<CreateUserRequest>({
    email: '',
    firstName: '',
    lastName: '',
    password: '',
    userLevelId: '',
    preferredLanguage: 'en',
    timeZone: 'UTC',
    roleName: 'Member',
  });
  
  const [editForm, setEditForm] = useState<UpdateUserRequest>({
    firstName: '',
    lastName: '',
    preferredLanguage: 'en',
    timeZone: 'UTC',
    userLevelId: '',
    profilePictureUrl: '',
  });
  
  const [newSkill, setNewSkill] = useState<AssignSkillRequest>({
    skillId: '',
    proficiencyLevel: 'Beginner',
    yearsOfExperience: 0,
  });

  useEffect(() => {
    loadData();
  }, [filterLevel, filterSkill]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [usersData, levelsData, skillsData] = await Promise.all([
        getUsers(filterLevel || undefined, filterSkill || undefined),
        getUserLevels(),
        getSkills(),
      ]);
      setUsers(usersData);
      setUserLevels(levelsData);
      setSkills(skillsData);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load users');
      console.error('Error loading data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditClick = (user: User) => {
    setSelectedUser(user);
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      preferredLanguage: user.preferredLanguage,
      timeZone: user.timeZone,
      userLevelId: user.userLevelId || '',
      profilePictureUrl: user.profilePictureUrl || '',
    });
    setEditDialogOpen(true);
  };

  const handleEditSave = async () => {
    if (!selectedUser) return;
    
    try {
      await updateUser(selectedUser.id, editForm);
      setEditDialogOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to update user');
    }
  };

  const handleCreateClick = () => {
    setCreateForm({
      email: '',
      firstName: '',
      lastName: '',
      password: '',
      userLevelId: '',
      preferredLanguage: 'en',
      timeZone: 'UTC',
      roleName: 'Member',
    });
    setCreateDialogOpen(true);
  };

  const handleCreateSave = async () => {
    try {
      await createUser(createForm);
      setCreateDialogOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to create user');
    }
  };

  const handleManageSkills = async (user: User) => {
    try {
      const fullUser = await getUser(user.id);
      setSelectedUser(fullUser);
      setSkillsDialogOpen(true);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load user details');
    }
  };

  const handleAddSkill = () => {
    setNewSkill({
      skillId: '',
      proficiencyLevel: 'Beginner',
      yearsOfExperience: 0,
    });
    setAddSkillDialogOpen(true);
  };

  const handleAddSkillSave = async () => {
    if (!selectedUser || !newSkill.skillId) return;
    
    try {
      await assignSkill(selectedUser.id, newSkill);
      const updatedUser = await getUser(selectedUser.id);
      setSelectedUser(updatedUser);
      setAddSkillDialogOpen(false);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to assign skill');
    }
  };

  const handleRemoveSkill = async (skillId: string) => {
    if (!selectedUser) return;
    
    try {
      await removeUserSkill(selectedUser.id, skillId);
      const updatedUser = await getUser(selectedUser.id);
      setSelectedUser(updatedUser);
      loadData();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to remove skill');
    }
  };

  const getProficiencyColor = (level: string) => {
    switch (level) {
      case 'Expert': return 'error';
      case 'Advanced': return 'warning';
      case 'Intermediate': return 'info';
      default: return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">
          {t('users.title', 'Users')}
        </Typography>
        <Button
          variant="contained"
          startIcon={<PersonAddIcon />}
          onClick={handleCreateClick}
        >
          {t('users.addUser', 'Add User')}
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label={t('users.filterByLevel', 'Filter by Level')}
              value={filterLevel}
              onChange={(e) => setFilterLevel(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('users.allLevels', 'All Levels')}</em>
              </MenuItem>
              {userLevels.map((level) => (
                <MenuItem key={level.id} value={level.id}>
                  {level.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
          <Grid item xs={12} md={4}>
            <TextField
              select
              fullWidth
              label={t('users.filterBySkill', 'Filter by Skill')}
              value={filterSkill}
              onChange={(e) => setFilterSkill(e.target.value)}
            >
              <MenuItem value="">
                <em>{t('users.allSkills', 'All Skills')}</em>
              </MenuItem>
              {skills.map((skill) => (
                <MenuItem key={skill.id} value={skill.id}>
                  {skill.icon} {skill.name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        </Grid>
      </Paper>

      {/* Users Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>{t('users.user', 'User')}</TableCell>
              <TableCell>{t('users.email', 'Email')}</TableCell>
              <TableCell>{t('users.level', 'Level')}</TableCell>
              <TableCell>{t('users.skills', 'Skills')}</TableCell>
              <TableCell align="right">{t('common.actions', 'Actions')}</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Typography color="textSecondary">
                    {t('users.noUsers', 'No users found')}
                  </Typography>
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow key={user.id} hover sx={{ cursor: 'pointer' }} onClick={() => navigate(`/users/${user.id}`)}>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={1}>
                      <Avatar src={user.profilePictureUrl} sx={{ width: 32, height: 32 }}>
                        {user.firstName[0]}{user.lastName[0]}
                      </Avatar>
                      <Typography>{user.fullName}</Typography>
                    </Box>
                  </TableCell>
                  <TableCell>{user.email}</TableCell>
                  <TableCell>
                    {user.userLevel ? (
                      <Chip
                        label={user.userLevel.name}
                        size="small"
                        sx={{
                          backgroundColor: user.userLevel.color || '#757575',
                          color: 'white',
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="textSecondary">
                        {t('users.noLevel', 'No level')}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap={0.5} flexWrap="wrap">
                      {user.skills.slice(0, 3).map((userSkill) => (
                        <Chip
                          key={userSkill.id}
                          label={userSkill.skill?.name || ''}
                          size="small"
                          color={getProficiencyColor(userSkill.proficiencyLevel)}
                        />
                      ))}
                      {user.skills.length > 3 && (
                        <Chip label={`+${user.skills.length - 3}`} size="small" variant="outlined" />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title={t('users.editUser', 'Edit User')}>
                      <IconButton size="small" onClick={() => handleEditClick(user)}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t('users.manageSkills', 'Manage Skills')}>
                      <IconButton size="small" onClick={() => handleManageSkills(user)}>
                        <PersonAddIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Edit User Dialog */}
      <Dialog open={editDialogOpen} onClose={() => setEditDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('users.editUser', 'Edit User')}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label={t('users.firstName', 'First Name')}
              value={editForm.firstName}
              onChange={(e) => setEditForm({ ...editForm, firstName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label={t('users.lastName', 'Last Name')}
              value={editForm.lastName}
              onChange={(e) => setEditForm({ ...editForm, lastName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              select
              label={t('users.level', 'Level')}
              value={editForm.userLevelId}
              onChange={(e) => setEditForm({ ...editForm, userLevelId: e.target.value })}
              fullWidth
            >
              <MenuItem value="">
                <em>{t('users.noLevel', 'No Level')}</em>
              </MenuItem>
              {userLevels.map((level) => (
                <MenuItem key={level.id} value={level.id}>
                  {level.name}
                </MenuItem>
              ))}
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleEditSave} variant="contained">
            {t('common.save', 'Save')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manage Skills Dialog */}
      <Dialog open={skillsDialogOpen} onClose={() => setSkillsDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {t('users.manageSkills', 'Manage Skills')} - {selectedUser?.fullName}
        </DialogTitle>
        <DialogContent>
          <Box mb={2}>
            <Button
              variant="outlined"
              startIcon={<AddIcon />}
              onClick={handleAddSkill}
              fullWidth
            >
              {t('users.addSkill', 'Add Skill')}
            </Button>
          </Box>
          
          <Grid container spacing={2}>
            {selectedUser?.skills.length === 0 ? (
              <Grid item xs={12}>
                <Typography color="textSecondary" align="center">
                  {t('users.noSkills', 'No skills assigned')}
                </Typography>
              </Grid>
            ) : (
              selectedUser?.skills.map((userSkill) => (
                <Grid item xs={12} md={6} key={userSkill.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="start">
                        <Box>
                          <Typography variant="h6">
                            {userSkill.skill?.icon} {userSkill.skill?.name}
                          </Typography>
                          <Typography variant="body2" color="textSecondary">
                            {userSkill.skill?.category}
                          </Typography>
                          <Box mt={1}>
                            <Chip
                              label={userSkill.proficiencyLevel}
                              size="small"
                              color={getProficiencyColor(userSkill.proficiencyLevel)}
                            />
                            {userSkill.yearsOfExperience && (
                              <Chip
                                label={`${userSkill.yearsOfExperience} years`}
                                size="small"
                                variant="outlined"
                                sx={{ ml: 1 }}
                              />
                            )}
                          </Box>
                        </Box>
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => handleRemoveSkill(userSkill.skillId)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkillsDialogOpen(false)}>{t('common.close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      {/* Add Skill Dialog */}
      <Dialog open={addSkillDialogOpen} onClose={() => setAddSkillDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('users.addSkill', 'Add Skill')}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              select
              label={t('users.skill', 'Skill')}
              value={newSkill.skillId}
              onChange={(e) => setNewSkill({ ...newSkill, skillId: e.target.value })}
              fullWidth
              required
            >
              {skills.map((skill) => (
                <MenuItem key={skill.id} value={skill.id}>
                  {skill.icon} {skill.name} ({skill.category})
                </MenuItem>
              ))}
            </TextField>
            <TextField
              select
              label={t('users.proficiency', 'Proficiency Level')}
              value={newSkill.proficiencyLevel}
              onChange={(e) => setNewSkill({ ...newSkill, proficiencyLevel: e.target.value })}
              fullWidth
              required
            >
              {PROFICIENCY_LEVELS.map((level) => (
                <MenuItem key={level} value={level}>
                  {level}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              type="number"
              label={t('users.yearsOfExperience', 'Years of Experience')}
              value={newSkill.yearsOfExperience || 0}
              onChange={(e) => setNewSkill({ ...newSkill, yearsOfExperience: parseInt(e.target.value) || 0 })}
              fullWidth
              inputProps={{ min: 0, max: 50 }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddSkillDialogOpen(false)}>{t('common.cancel', 'Cancel')}</Button>
          <Button onClick={handleAddSkillSave} variant="contained">
            {t('common.add', 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog open={createDialogOpen} onClose={() => setCreateDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create New User</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Email"
              type="email"
              value={createForm.email}
              onChange={(e) => setCreateForm({ ...createForm, email: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="First Name"
              value={createForm.firstName}
              onChange={(e) => setCreateForm({ ...createForm, firstName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Last Name"
              value={createForm.lastName}
              onChange={(e) => setCreateForm({ ...createForm, lastName: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Password (optional - auto-generated if empty)"
              type="password"
              value={createForm.password}
              onChange={(e) => setCreateForm({ ...createForm, password: e.target.value })}
              fullWidth
              helperText="Leave empty to auto-generate a secure password"
            />
            <TextField
              select
              label="User Level"
              value={createForm.userLevelId || ''}
              onChange={(e) => setCreateForm({ ...createForm, userLevelId: e.target.value })}
              fullWidth
            >
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
              {userLevels.map((level) => (
                <MenuItem key={level.id} value={level.id}>
                  {level.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Role"
              value={createForm.roleName}
              onChange={(e) => setCreateForm({ ...createForm, roleName: e.target.value })}
              fullWidth
              helperText="e.g., Member, Admin, Manager"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleCreateSave} variant="contained" disabled={!createForm.email || !createForm.firstName || !createForm.lastName}>
            Create User
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
